<template>
  <Layout>
    <section class="home-hero">
      <carouselHeader />
      <div class="home-hero__overlay">
        <div class="content-wrap home-hero__content">
          <span class="eyebrow">AfM · seit 1977</span>
          <h1 v-if="sprachID === 0">Arbeitskreis für Mammillarienfreunde</h1>
          <h1 v-else>Society for Mammillaria enthusiasts</h1>
          <p v-if="sprachID === 0">Wissen teilen, Pflanzen erforschen und Menschen verbinden – rund um Mammillaria und verwandte Gattungen.</p>
          <p v-else>Sharing knowledge, exploring plants and connecting people around Mammillaria and related genera.</p>
        </div>
      </div>
    </section>

    <section class="section section--white">
      <div class="content-wrap intro-grid">
        <div>
          <span class="section-kicker" v-if="sprachID === 0">Über uns</span>
          <span class="section-kicker" v-else>About us</span>
          <h2 v-if="sprachID === 0">Eine internationale Gemeinschaft mit Tradition</h2>
          <h2 v-else>An international community with a long tradition</h2>
        </div>
        <div class="prose-large">
          <template v-if="sprachID === 0">
            <p>Der Arbeitskreis für Mammillarienfreunde ist eine Gesellschaft, die sich ausschließlich mit der Kakteengattung Mammillaria und den verwandten Gattungen Coryphantha, Escobaria und Neolloydia befasst.</p>
            <p>Der AfM wurde 1977 von neun deutschen Mammillarien-Liebhabern gegründet und am 12. Dezember im Vereinsregister der Stadt Münster eingetragen. Heute hat der AfM ca. 300 Mitglieder in 18 Ländern. Dazu zählen neben Einzelpersonen auch Botanische Gärten und andere Institutionen in Europa und Übersee.</p>
            <p>1990 vereinigten sich die ZAG-Mammillaria aus der ehemaligen DDR und der AfM. Das Mitteilungsblatt <strong>MAMMILLARIA</strong> ist bis heute ein wichtiges Kommunikations- und Arbeitsmittel für den Austausch von Untersuchungen am Standort, im Gewächshaus und in der Bibliothek.</p>
          </template>
          <template v-else>
            <p>The “Arbeitskreis für Mammillarienfreunde” is a cactus society devoted exclusively to Mammillaria and the related genera Coryphantha, Escobaria and Neolloydia.</p>
            <p>The AfM was founded in 1977 by nine German Mammillaria enthusiasts and officially registered in Münster on December 12. Today the AfM has about 300 members in 18 countries, including individuals, botanical gardens and institutions in Europe and overseas.</p>
            <p>In 1990 ZAG-Mammillaria from the former GDR and the AfM joined forces. The journal <strong>MAMMILLARIA</strong> remains an important medium for members to exchange research from natural habitats, greenhouses and libraries.</p>
          </template>
        </div>
      </div>
    </section>

    <section class="section section--soft">
      <div class="content-wrap">
        <article class="anniversary-banner">
          <div class="anniversary-banner__icon"><svg class="icon-svg" viewBox="0 0 24 24" aria-hidden="true"><path d="M7 2v3M17 2v3M3.5 9.5h17M5.5 4.5h13a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2v-12a2 2 0 0 1 2-2Z"/><path d="m8 15 2.2 2.2L16 11.5"/></svg></div>
          <div>
            <span class="section-kicker">Save the date</span>
            <h2>50. JHV des AfM</h2>
            <p>30.04. – 02.05.2027 · Raum Münster</p>
          </div>
        </article>

        <div class="section-heading">
          <div>
            <span class="section-kicker" v-if="sprachID === 0">Aktuell</span>
            <span class="section-kicker" v-else>Current</span>
            <h2 v-if="sprachID === 0">Neuigkeiten & Termine</h2>
            <h2 v-else>News & dates</h2>
          </div>
        </div>

        <div class="news-grid">
          <article class="feature-card feature-card--dark">
            <div class="feature-card__icon"><svg class="icon-svg" viewBox="0 0 24 24" aria-hidden="true"><path d="M7 2v3M17 2v3M3.5 9.5h17M5.5 4.5h13a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2v-12a2 2 0 0 1 2-2Z"/><path d="M8 13h3M8 17h3M14 13h2"/></svg></div>
            <span class="feature-card__label" v-if="sprachID === 0">Herbsttagung</span>
            <span class="feature-card__label" v-else>Autumn conference</span>
            <h3 v-if="sprachID === 0">Wächtersbach / Aufenau</h3>
            <h3 v-else>Wächtersbach / Aufenau</h3>
            <p v-if="sprachID === 0">02. – 04.10.2026</p>
            <p v-else>October 2 – 4, 2026</p>
            <router-link to="/Veranstaltungen" class="card-link">{{ sprachID === 0 ? 'Zu den Veranstaltungen' : 'View events' }} <svg class="icon-svg icon-svg--link" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h13M14 7l5 5-5 5"/></svg></router-link>
          </article>

          <article class="feature-card feature-card--journal">
            <div class="journal-badge">
              <svg class="icon-svg" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 3.5h11.5A2.5 2.5 0 0 1 19 6v14.5H7.5A2.5 2.5 0 0 1 5 18V3.5Z"/><path d="M5 17.5c0-1.4 1.1-2.5 2.5-2.5H19M8.5 7h7M8.5 10h5"/></svg>
              <span>{{ sprachID === 0 ? 'Aktuelle Ausgabe' : 'Current issue' }}</span>
            </div>
            <div class="journal-layout">
              <div class="cover-wrap cover-wrap--journal">
                <img src="../assets/Mitteilungsblatt/Titelbild/2026-3.jpg" alt="Mitteilungsblatt MAMMILLARIA 3.2026" />
              </div>
              <div class="journal-copy">
                <span class="feature-card__label">Mitteilungsblatt · 3.2026</span>
                <h3>MAMMILLARIA</h3>
                <p v-if="sprachID === 0">Das aktuelle Mitteilungsblatt des AfM mit Fachbeiträgen, Beobachtungen und Informationen aus dem Arbeitskreis.</p>
                <p v-else>The current AfM journal with specialist articles, observations and news from the society.</p>
                <div class="button-row">
                  <button class="btn-modern btn-modern--primary" @click="openPDF('Probeartikel/Probeartikel_3_2026.pdf')">
                    <svg class="icon-svg icon-svg--button" viewBox="0 0 24 24" aria-hidden="true"><path d="M7 3.5h7l4 4V20H7V3.5Z"/><path d="M14 3.5V8h4M10 12h5M10 15h5"/></svg>
                    {{ sprachID === 0 ? 'Probeartikel lesen' : 'Read sample article' }}
                  </button>
                  <button class="btn-modern btn-modern--ghost" @click="openPDF('IHV/IHVHeft32026.pdf')">
                    <svg class="icon-svg icon-svg--button" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 5h12M6 10h12M6 15h8M6 20h5"/></svg>
                    {{ sprachID === 0 ? 'Inhaltsverzeichnis' : 'Contents' }}
                  </button>
                </div>
              </div>
            </div>
          </article>

          <article class="feature-card feature-card--accent">
            <div class="feature-card__icon"><svg class="icon-svg" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21v-9M12 13c-4.5 0-7-2.4-7-6.5 4.7 0 7 2.2 7 6.5ZM12 16c4.5 0 7-2.4 7-6.5-4.7 0-7 2.2-7 6.5Z"/></svg></div>
            <span class="feature-card__label" v-if="sprachID === 0">Samenfonds</span>
            <span class="feature-card__label" v-else>Seed fund</span>
            <h3 v-if="sprachID === 0">Samenliste 2026 verfügbar</h3>
            <h3 v-else>Seed list 2026 available</h3>
            <p v-if="sprachID === 0">Der Samenfonds bittet außerdem um Samenspenden.</p>
            <p v-else>The seed fund also welcomes seed donations.</p>
            <router-link to="/Shop" class="card-link">{{ sprachID === 0 ? 'Zum Samenfonds' : 'Seed fund' }} <svg class="icon-svg icon-svg--link" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h13M14 7l5 5-5 5"/></svg></router-link>
          </article>
        </div>
      </div>
    </section>

    <section class="section section--white">
      <div class="content-wrap">
        <div class="section-heading section-heading--split">
          <div>
            <span class="section-kicker" v-if="sprachID === 0">Mitmachen</span>
            <span class="section-kicker" v-else>Get involved</span>
            <h2 v-if="sprachID === 0">Unser Facebook-Auftritt braucht Ihre Bilder</h2>
            <h2 v-else>Our Facebook presence needs your photos</h2>
          </div>
          <button class="btn-modern btn-modern--primary" @click="openFacebook()">
            <svg class="icon-svg icon-svg--button" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 16V4M7.5 8.5 12 4l4.5 4.5M5 14v5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-5"/></svg> {{ sprachID === 0 ? 'Bilder hochladen' : 'Upload pictures' }}
          </button>
        </div>
      </div>
    </section>

    <section class="section section--dark">
      <div class="content-wrap">
        <div class="section-heading section-heading--light">
          <div>
            <span class="section-kicker" v-if="sprachID === 0">Für Mitglieder</span>
            <span class="section-kicker" v-else>For members</span>
            <h2 v-if="sprachID === 0">Unsere Leistungen</h2>
            <h2 v-else>Our services</h2>
          </div>
        </div>

        <div class="services-grid">
          <article class="service-card">
            <svg class="icon-svg service-icon-svg" viewBox="0 0 24 24" aria-hidden="true"><path d="M3.5 5.5c3.4-.8 6-.3 8.5 1.5v12c-2.5-1.8-5.1-2.3-8.5-1.5v-12ZM20.5 5.5c-3.4-.8-6-.3-8.5 1.5v12c2.5-1.8 5.1-2.3 8.5-1.5v-12Z"/></svg>
            <h3>{{ sprachID === 0 ? 'Loseblattsammlung' : 'Loose-leaf collection' }}</h3>
            <p v-if="sprachID === 0">Ein ausführliches Sammelwerk zu Arten, Unterarten, Varietäten und Synonymen, das jährlich ergänzt wird.</p>
            <p v-else>A detailed collection of species, subspecies, varieties and synonyms, updated every year.</p>
          </article>
          <article class="service-card">
            <svg class="icon-svg service-icon-svg" viewBox="0 0 24 24" aria-hidden="true"><circle cx="9" cy="8" r="3"/><circle cx="17" cy="9" r="2.3"/><path d="M3.5 19c.6-4 2.4-6 5.5-6s4.9 2 5.5 6M14 14c2.8 0 4.7 1.5 5.5 4.5"/></svg>
            <h3>{{ sprachID === 0 ? 'Tagungen' : 'Conferences' }}</h3>
            <p v-if="sprachID === 0">Jährliche Treffen im Frühjahr und Herbst für Vorträge, Erfahrungsaustausch und persönliche Begegnungen.</p>
            <p v-else>Annual spring and autumn meetings for lectures, exchange of experience and personal encounters.</p>
          </article>
          <article class="service-card">
            <svg class="icon-svg service-icon-svg" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 3.5h11.5A2.5 2.5 0 0 1 19 6v14.5H7.5A2.5 2.5 0 0 1 5 18V3.5Z"/><path d="M5 17.5c0-1.4 1.1-2.5 2.5-2.5H19M8.5 7h7M8.5 10h5"/></svg>
            <h3>{{ sprachID === 0 ? 'Bibliothek' : 'Library' }}</h3>
            <p v-if="sprachID === 0">Mitglieder können Bücher, Zeitschriften und andere Veröffentlichungen aus der ständig wachsenden Vereinsbibliothek ausleihen.</p>
            <p v-else>Members can borrow books, journals and other publications from the continuously growing club library.</p>
          </article>
          <article class="service-card">
            <svg class="icon-svg service-icon-svg" viewBox="0 0 24 24" aria-hidden="true"><path d="M20 4C12 4 6 7.5 6 13c0 3.4 2.6 6 6 6 5.5 0 8-6 8-15Z"/><path d="M4 20c3-5.5 7-8.5 12-11"/></svg>
            <h3>{{ sprachID === 0 ? 'Samenfonds' : 'Seed fund' }}</h3>
            <p v-if="sprachID === 0">Gespendetes Saatgut wird aufbereitet und über die im Shop veröffentlichte Samenliste angeboten.</p>
            <p v-else>Donated seeds are prepared and offered through the seed list published in the shop.</p>
          </article>
          <article class="service-card">
            <svg class="icon-svg service-icon-svg" viewBox="0 0 24 24" aria-hidden="true"><path d="m10 3 4 4-2 2-4-4 2-2ZM11 8l-3.5 3.5a4 4 0 0 0 5.7 5.7l2.3-2.3M14 14.5A6.5 6.5 0 0 1 8 21h10M5 21h14"/></svg>
            <h3>{{ sprachID === 0 ? 'REM-Archiv' : 'SEM archive' }}</h3>
            <p v-if="sprachID === 0">Eine wachsende Sammlung von Raster-Elektronen-Mikroskop-Aufnahmen von Samen steht Mitgliedern bei Bedarf zur Verfügung.</p>
            <p v-else>A growing collection of scanning electron microscope images of seeds is available to members on request.</p>
          </article>
        </div>
      </div>
    </section>

    <section class="section section--gallery">
      <div class="content-wrap">
        <div class="section-heading">
          <div>
            <span class="section-kicker" v-if="sprachID === 0">Impressionen</span>
            <span class="section-kicker" v-else>Impressions</span>
            <h2 v-if="sprachID === 0">Mammillarien entdecken</h2>
            <h2 v-else>Discover Mammillaria</h2>
          </div>
        </div>
        <div class="desktop-carousel"><carouselHome /></div>
        <div class="mobile-carousel"><carouselHomeHandy /></div>
      </div>
    </section>
  </Layout>
</template>

<script>
import Layout from "@/layouts/Layout";
import carouselHome from "../components/carouselHome.vue";
import carouselHomeHandy from "../components/carouselHomeHandy.vue";
import carouselHeader from "../components/carouselHeader.vue";
import { openModal } from "jenesius-vue-modal";
import modalPDF from "../components/ModalPDF.vue";
import modalFacebook from "../components/ModalFacebook.vue";

export default {
  name: "Start",
  computed: {
    sprachID() {
      return this.$store.getters.getSprachID;
    },
  },
  components: { Layout, carouselHome, carouselHomeHandy, carouselHeader },
  methods: {
    openPDF(pdf) {
      openModal(modalPDF, { pdf });
    },
    openFacebook() {
      openModal(modalFacebook);
    },
  },
};
</script>
